import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useOrganizationSettings,
  useUpdateOrganizationSettings,
  useHolidays,
  QUERY_KEYS,
} from "../../hooks/useOrganizationSettings";
import { useDebounce } from "../../hooks/useDebounce";
import { useAdminOrganizations } from "../../hooks/useAdminOrganizations";
import { apiService } from "../../services/api";
import { toast } from "../../hooks/use-toast";
import { getApiErrorMessage, getErrorMessage } from "../../lib/apiErrors";
import type { UpdateOrganizationSettingsRequest } from "../../types/organizations";
import type {
  OrgSettingsFormState,
  EmailValidationState,
  PortalKeyStatus,
  PendingHolidayChanges,
} from "./types";
import { DEFAULT_FORM_STATE, createFormStateFromSettings } from "./types";

interface OrgSettingsContextValue {
  // Organization info
  organizationId: string;
  organizationName: string | undefined;
  organizationSubdomain: string | undefined;
  organizationDescription: string | undefined;

  // Form state
  formState: OrgSettingsFormState;
  updateFormState: (updates: Partial<OrgSettingsFormState>) => void;
  resetFormState: () => void;

  // Dirty tracking
  isDirty: boolean;
  hasPendingHolidayChanges: boolean;

  // Save operations
  saveSettings: () => Promise<void>;
  isSaving: boolean;

  // Loading states
  isLoading: boolean;
  isLoadingSettings: boolean;
  isLoadingHolidays: boolean;

  // Email validation
  emailValidation: EmailValidationState;

  // Portal key management
  portalKeyStatus: PortalKeyStatus | null;
  generatedPortalKey: string | null;
  isLoadingKeyStatus: boolean;
  isGeneratingKey: boolean;
  isRevokingKey: boolean;
  generatePortalKey: () => Promise<void>;
  revokePortalKey: () => Promise<void>;
  copyPortalKey: (key: string) => void;
  clearGeneratedKey: () => void;

  // Holiday management (pending changes for explicit save)
  pendingHolidayChanges: PendingHolidayChanges;
  addPendingHoliday: (holiday: PendingHolidayChanges["toAdd"][0]) => void;
  removePendingHoliday: (index: number) => void;
  markHolidayForRemoval: (holidayId: string) => void;
  unmarkHolidayForRemoval: (holidayId: string) => void;
  serverHolidays: ReturnType<typeof useHolidays>["data"];
}

const OrgSettingsContext = createContext<OrgSettingsContextValue | null>(null);

interface OrgSettingsProviderProps {
  organizationId: string;
  children: React.ReactNode;
}

export function OrgSettingsProvider({
  organizationId,
  children,
}: OrgSettingsProviderProps) {
  const queryClient = useQueryClient();

  // Fetch organization data
  const { data: adminOrgs, isLoading: isLoadingOrgs } = useAdminOrganizations();
  const currentOrg = adminOrgs?.find((org) => org.id === organizationId);

  // Fetch settings and holidays
  const { data: settings, isLoading: isLoadingSettings } =
    useOrganizationSettings(organizationId);
  const { data: holidays, isLoading: isLoadingHolidays } =
    useHolidays(organizationId);
  const updateSettingsMutation = useUpdateOrganizationSettings(organizationId);

  // Form state
  const [formState, setFormState] =
    useState<OrgSettingsFormState>(DEFAULT_FORM_STATE);
  const [isDirty, setIsDirty] = useState(false);

  // Email validation
  const [emailValidation, setEmailValidation] = useState<EmailValidationState>({
    isValidating: false,
    isValid: null,
    error: null,
  });
  const debouncedEmailPrefix = useDebounce(formState.fromEmailPrefix, 500);

  // Portal key state
  const [portalKeyStatus, setPortalKeyStatus] =
    useState<PortalKeyStatus | null>(null);
  const [generatedPortalKey, setGeneratedPortalKey] = useState<string | null>(
    null
  );
  const [isLoadingKeyStatus, setIsLoadingKeyStatus] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [isRevokingKey, setIsRevokingKey] = useState(false);

  // Pending holiday changes
  const [pendingHolidayChanges, setPendingHolidayChanges] =
    useState<PendingHolidayChanges>({
      toAdd: [],
      toRemove: [],
    });

  const hasPendingHolidayChanges =
    pendingHolidayChanges.toAdd.length > 0 ||
    pendingHolidayChanges.toRemove.length > 0;

  // Initialize form state when settings load
  useEffect(() => {
    if (settings) {
      setFormState(createFormStateFromSettings(settings));
      setIsDirty(false);
      // Reset pending holiday changes when settings reload
      setPendingHolidayChanges({ toAdd: [], toRemove: [] });
    }
  }, [settings]);

  // Load portal key status
  useEffect(() => {
    const loadPortalKeyStatus = async () => {
      if (!organizationId) return;

      setIsLoadingKeyStatus(true);
      try {
        const response =
          await apiService.getPublicPortalKeyStatus(organizationId);
        if (response.success && response.data) {
          setPortalKeyStatus({
            hasPortalKey: response.data.hasPublicPortalKey,
            maskedPortalKey: response.data.maskedPublicPortalKey,
            createdAt: response.data.createdAt,
          });
        }
      } catch (error) {
        console.error("Failed to load public portal key status:", error);
      } finally {
        setIsLoadingKeyStatus(false);
      }
    };

    loadPortalKeyStatus();
  }, [organizationId]);

  // Email validation
  useEffect(() => {
    const validateEmail = async () => {
      if (!debouncedEmailPrefix || !organizationId) {
        setEmailValidation({ isValidating: false, isValid: null, error: null });
        return;
      }

      if (debouncedEmailPrefix.trim() === "") {
        setEmailValidation({ isValidating: false, isValid: null, error: null });
        return;
      }

      setEmailValidation({ isValidating: true, isValid: null, error: null });

      try {
        const fullEmailAddress = `${debouncedEmailPrefix}@euserviceline.com`;
        const response = await apiService.validateFromEmailAddress(
          fullEmailAddress,
          organizationId
        );

        if (response.success) {
          setEmailValidation({
            isValidating: false,
            isValid: response.data || false,
            error: null,
          });
        } else {
          setEmailValidation({
            isValidating: false,
            isValid: false,
            error: getApiErrorMessage(response, "Failed to validate email address"),
          });
        }
      } catch {
        setEmailValidation({
          isValidating: false,
          isValid: false,
          error: "Failed to validate email address",
        });
      }
    };

    validateEmail();
  }, [debouncedEmailPrefix, organizationId]);

  // Form state update handler
  const updateFormState = useCallback(
    (updates: Partial<OrgSettingsFormState>) => {
      setFormState((prev) => ({ ...prev, ...updates }));
      setIsDirty(true);
    },
    []
  );

  // Reset form state
  const resetFormState = useCallback(() => {
    if (settings) {
      setFormState(createFormStateFromSettings(settings));
      setIsDirty(false);
      setPendingHolidayChanges({ toAdd: [], toRemove: [] });
    }
  }, [settings]);

  // Save settings (including holiday changes)
  const saveSettings = useCallback(async () => {
    if (!organizationId) {
      toast({
        title: "Error",
        description: "Organization ID not found.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Save organization settings
      const updateData: UpdateOrganizationSettingsRequest = {
        logoUrl: formState.logoUrl,
        primaryColor: formState.primaryColor,
        secondaryColor: formState.secondaryColor,
        fromEmailPrefix: formState.fromEmailPrefix,
        fromEmailName: formState.fromEmailName,
        inboundEmailEnabled: formState.inboundEmailEnabled,
        autoCreateCustomers: formState.autoCreateCustomers,
        allowCustomerSelfRegistration: formState.allowCustomerSelfRegistration,
        isKnowledgeBasePublic: formState.isKnowledgeBasePublic,
        articleReviewIntervalDays: formState.articleReviewIntervalDays,
        autoCloseDays: formState.autoCloseDays,
        requireCategoryOnResolve: formState.requireCategoryOnResolve,
        aiClassificationEnabled: formState.aiClassificationEnabled,
        timeZoneId: formState.timeZoneId,
        workDayStart: formState.workDayStart,
        workDayEnd: formState.workDayEnd,
        workingDays: formState.workingDays,
      };

      // A failure here is toasted by the mutation's onError
      await updateSettingsMutation.mutateAsync(updateData);
    } catch (error) {
      console.error("Failed to save organization settings:", error);
      return;
    }

    // Save holiday changes via batch endpoint. The settings above are already saved (and their
    // success toast shown), so a holiday failure needs its own error toast.
    if (hasPendingHolidayChanges) {
      const response = await apiService.batchUpdateHolidays(
        organizationId,
        pendingHolidayChanges
      );
      if (!response.success) {
        toast({
          title: "Holidays Not Saved",
          description: getApiErrorMessage(response, "Failed to save holiday changes."),
          variant: "destructive",
        });
        return;
      }
      // Invalidate holidays cache to refresh the list
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.HOLIDAYS(organizationId),
      });
    }

    setIsDirty(false);
    setPendingHolidayChanges({ toAdd: [], toRemove: [] });
  }, [
    organizationId,
    formState,
    updateSettingsMutation,
    hasPendingHolidayChanges,
    pendingHolidayChanges,
    queryClient,
  ]);

  // Portal key operations
  const generatePortalKey = useCallback(async () => {
    if (!organizationId) return;

    setIsGeneratingKey(true);
    try {
      const response =
        await apiService.generatePublicPortalKey(organizationId);
      if (response.success && response.data) {
        setGeneratedPortalKey(response.data.publicPortalKey);
        setPortalKeyStatus({
          hasPortalKey: true,
          maskedPortalKey: response.data.maskedPublicPortalKey,
          createdAt: response.data.createdAt,
        });
        toast({
          title: "Success",
          description:
            "Public portal key generated successfully. Make sure to copy it now - you won't be able to see it again!",
        });
      } else {
        toast({
          title: "Error",
          description: getApiErrorMessage(response, "Failed to generate public portal key"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to generate public portal key:", error);
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to generate public portal key"),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingKey(false);
    }
  }, [organizationId]);

  const revokePortalKey = useCallback(async () => {
    if (!organizationId) return;

    if (
      !confirm(
        "Are you sure you want to revoke the public portal key? This will immediately break any external portal integration using this key."
      )
    ) {
      return;
    }

    setIsRevokingKey(true);
    try {
      const response = await apiService.revokePublicPortalKey(organizationId);
      if (response.success) {
        setPortalKeyStatus({
          hasPortalKey: false,
          maskedPortalKey: null,
          createdAt: null,
        });
        setGeneratedPortalKey(null);
        toast({
          title: "Success",
          description: "Public portal key revoked successfully",
        });
      } else {
        toast({
          title: "Error",
          description: getApiErrorMessage(response, "Failed to revoke public portal key"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to revoke public portal key:", error);
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to revoke public portal key"),
        variant: "destructive",
      });
    } finally {
      setIsRevokingKey(false);
    }
  }, [organizationId]);

  const copyPortalKey = useCallback((key: string) => {
    navigator.clipboard.writeText(key);
    toast({
      title: "Copied",
      description: "Public portal key copied to clipboard",
    });
  }, []);

  const clearGeneratedKey = useCallback(() => {
    setGeneratedPortalKey(null);
  }, []);

  // Holiday management
  const addPendingHoliday = useCallback(
    (holiday: PendingHolidayChanges["toAdd"][0]) => {
      setPendingHolidayChanges((prev) => ({
        ...prev,
        toAdd: [...prev.toAdd, holiday],
      }));
      setIsDirty(true);
    },
    []
  );

  const removePendingHoliday = useCallback((index: number) => {
    setPendingHolidayChanges((prev) => ({
      ...prev,
      toAdd: prev.toAdd.filter((_, i) => i !== index),
    }));
  }, []);

  const markHolidayForRemoval = useCallback((holidayId: string) => {
    setPendingHolidayChanges((prev) => ({
      ...prev,
      toRemove: [...prev.toRemove, holidayId],
    }));
    setIsDirty(true);
  }, []);

  const unmarkHolidayForRemoval = useCallback((holidayId: string) => {
    setPendingHolidayChanges((prev) => ({
      ...prev,
      toRemove: prev.toRemove.filter((id) => id !== holidayId),
    }));
  }, []);

  const value = useMemo<OrgSettingsContextValue>(
    () => ({
      organizationId,
      organizationName: currentOrg?.name,
      organizationSubdomain: currentOrg?.subdomain,
      organizationDescription: currentOrg?.description,

      formState,
      updateFormState,
      resetFormState,

      isDirty: isDirty || hasPendingHolidayChanges,
      hasPendingHolidayChanges,

      saveSettings,
      isSaving: updateSettingsMutation.isPending,

      isLoading: isLoadingSettings || isLoadingOrgs,
      isLoadingSettings,
      isLoadingHolidays,

      emailValidation,

      portalKeyStatus,
      generatedPortalKey,
      isLoadingKeyStatus,
      isGeneratingKey,
      isRevokingKey,
      generatePortalKey,
      revokePortalKey,
      copyPortalKey,
      clearGeneratedKey,

      pendingHolidayChanges,
      addPendingHoliday,
      removePendingHoliday,
      markHolidayForRemoval,
      unmarkHolidayForRemoval,
      serverHolidays: holidays,
    }),
    [
      organizationId,
      currentOrg,
      formState,
      updateFormState,
      resetFormState,
      isDirty,
      hasPendingHolidayChanges,
      saveSettings,
      updateSettingsMutation.isPending,
      isLoadingSettings,
      isLoadingOrgs,
      isLoadingHolidays,
      emailValidation,
      portalKeyStatus,
      generatedPortalKey,
      isLoadingKeyStatus,
      isGeneratingKey,
      isRevokingKey,
      generatePortalKey,
      revokePortalKey,
      copyPortalKey,
      clearGeneratedKey,
      pendingHolidayChanges,
      addPendingHoliday,
      removePendingHoliday,
      markHolidayForRemoval,
      unmarkHolidayForRemoval,
      holidays,
    ]
  );

  return (
    <OrgSettingsContext.Provider value={value}>
      {children}
    </OrgSettingsContext.Provider>
  );
}

export function useOrgSettings() {
  const context = useContext(OrgSettingsContext);
  if (!context) {
    throw new Error("useOrgSettings must be used within OrgSettingsProvider");
  }
  return context;
}
